import type { DiagramDocument, DiagramEdge, DiagramNode, DiagramPage } from './types';
import { DEFAULT_NODE_STYLE } from './defaults';
import { SHAPE_DEFINITIONS } from './shapeRegistry';
import { normalizeDocument } from './normalize';
import { validateDocument } from './validation';

export interface RecoveryResult {
  ok: boolean;
  document?: DiagramDocument;
  changes: string[];
  errors: string[];
}

const NODE_TYPES = new Set(SHAPE_DEFINITIONS.map((shape) => shape.type));
const safeId = (value: unknown) => typeof value === 'string' && /^[A-Za-z0-9._:-]{1,160}$/.test(value);
const safeColor = (value: unknown, fallback: string) => typeof value === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(value) ? value : fallback;
const finite = (value: unknown, fallback: number) => typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const text = (value: unknown, fallback = '') => typeof value === 'string' ? value : fallback;

function uuid(): string { return crypto.randomUUID(); }

function recoverNode(raw: unknown, pageIndex: number, nodeIndex: number, changes: string[]): DiagramNode | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  if (!NODE_TYPES.has(source.type as never)) return null;
  const id = safeId(source.id) ? String(source.id) : uuid();
  if (id !== source.id) changes.push(`Página ${pageIndex + 1}: objeto ${nodeIndex + 1} recebeu um novo ID.`);
  const styleSource = source.style && typeof source.style === 'object' && !Array.isArray(source.style) ? source.style as Record<string, unknown> : {};
  const dataSource = source.data && typeof source.data === 'object' && !Array.isArray(source.data) ? source.data as Record<string, unknown> : {};
  const data: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(dataSource).slice(0, 200)) {
    if (!key || key.length > 100 || ['__proto__', 'prototype', 'constructor'].includes(key)) continue;
    if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
      if (typeof value !== 'number' || Number.isFinite(value)) data[key] = typeof value === 'string' ? value.slice(0, 10_000) : value as number | boolean | null;
    }
  }
  return {
    id,
    type: source.type as DiagramNode['type'],
    x: Math.max(-1_000_000, Math.min(1_000_000, finite(source.x, 120))),
    y: Math.max(-1_000_000, Math.min(1_000_000, finite(source.y, 120))),
    width: Math.max(1, Math.min(100_000, finite(source.width, 160))),
    height: Math.max(1, Math.min(100_000, finite(source.height, 80))),
    rotation: Math.max(-360_000, Math.min(360_000, finite(source.rotation, 0))),
    text: text(source.text).slice(0, 50_000),
    style: {
      ...DEFAULT_NODE_STYLE,
      fill: safeColor(styleSource.fill, DEFAULT_NODE_STYLE.fill),
      stroke: safeColor(styleSource.stroke, DEFAULT_NODE_STYLE.stroke),
      textColor: safeColor(styleSource.textColor, DEFAULT_NODE_STYLE.textColor),
      strokeWidth: Math.max(0.5, Math.min(20, finite(styleSource.strokeWidth, DEFAULT_NODE_STYLE.strokeWidth))),
      fontSize: Math.max(6, Math.min(200, finite(styleSource.fontSize, DEFAULT_NODE_STYLE.fontSize))),
      radius: Math.max(0, Math.min(600, finite(styleSource.radius, DEFAULT_NODE_STYLE.radius))),
      opacity: Math.max(0.05, Math.min(1, finite(styleSource.opacity, DEFAULT_NODE_STYLE.opacity))),
      linePattern: styleSource.linePattern === 'dashed' ? 'dashed' : 'solid',
      textAlign: ['left', 'center', 'right'].includes(String(styleSource.textAlign)) ? styleSource.textAlign as 'left' | 'center' | 'right' : DEFAULT_NODE_STYLE.textAlign,
      fontWeight: Math.max(100, Math.min(900, finite(styleSource.fontWeight, DEFAULT_NODE_STYLE.fontWeight))),
    },
    data,
    locked: typeof source.locked === 'boolean' ? source.locked : false,
    hidden: typeof source.hidden === 'boolean' ? source.hidden : false,
    groupId: safeId(source.groupId) ? String(source.groupId) : null,
    entityFields: Array.isArray(source.entityFields) ? structuredClone(source.entityFields) as DiagramNode['entityFields'] : undefined,
    umlAttributes: Array.isArray(source.umlAttributes) ? source.umlAttributes.filter((item): item is string => typeof item === 'string').slice(0, 500) : undefined,
    umlMethods: Array.isArray(source.umlMethods) ? source.umlMethods.filter((item): item is string => typeof item === 'string').slice(0, 500) : undefined,
  };
}

function recoverEdge(raw: unknown, nodeIds: Set<string>, remap: Map<string, string>, pageIndex: number, edgeIndex: number, changes: string[]): DiagramEdge | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  const sourceIdRaw = text(source.sourceId);
  const targetIdRaw = text(source.targetId);
  const sourceId = remap.get(sourceIdRaw) ?? sourceIdRaw;
  const targetId = remap.get(targetIdRaw) ?? targetIdRaw;
  if (!nodeIds.has(sourceId) || !nodeIds.has(targetId)) {
    changes.push(`Página ${pageIndex + 1}: conexão ${edgeIndex + 1} órfã foi removida.`);
    return null;
  }
  if (sourceId === targetId) {
    changes.push(`Página ${pageIndex + 1}: conexão ${edgeIndex + 1} ambígua/auto-conectada foi removida.`);
    return null;
  }
  return {
    id: safeId(source.id) ? String(source.id) : uuid(),
    sourceId,
    targetId,
    label: text(source.label).slice(0, 10_000),
    style: source.style === 'straight' ? 'straight' : 'orthogonal',
    stroke: safeColor(source.stroke, '#64748b'),
    strokeWidth: Math.max(0.5, Math.min(20, finite(source.strokeWidth, 2))),
    arrowEnd: typeof source.arrowEnd === 'boolean' ? source.arrowEnd : true,
    arrowStart: typeof source.arrowStart === 'boolean' ? source.arrowStart : false,
    startMarker: ['none','arrow','circle','diamond','square'].includes(String(source.startMarker)) ? source.startMarker as DiagramEdge['startMarker'] : undefined,
    endMarker: ['none','arrow','circle','diamond','square'].includes(String(source.endMarker)) ? source.endMarker as DiagramEdge['endMarker'] : undefined,
    sourceAnchor: ['auto','topLeft','top','topRight','rightTop','right','rightBottom','bottomRight','bottom','bottomLeft','leftBottom','left','leftTop'].includes(String(source.sourceAnchor)) ? source.sourceAnchor as DiagramEdge['sourceAnchor'] : 'auto',
    targetAnchor: ['auto','topLeft','top','topRight','rightTop','right','rightBottom','bottomRight','bottom','bottomLeft','leftBottom','left','leftTop'].includes(String(source.targetAnchor)) ? source.targetAnchor as DiagramEdge['targetAnchor'] : 'auto',
    linePattern: source.linePattern === 'dashed' ? 'dashed' : 'solid',
    routingMode: source.routingMode === 'manual' ? 'manual' : 'auto',
    waypoints: Array.isArray(source.waypoints) ? source.waypoints.filter((point): point is { x: number; y: number } => Boolean(point && typeof point === 'object' && Number.isFinite((point as {x?:unknown}).x) && Number.isFinite((point as {y?:unknown}).y))).slice(0, 200).map((point) => ({ x: point.x, y: point.y })) : [],
    sourceCardinality: source.sourceCardinality as DiagramEdge['sourceCardinality'],
    targetCardinality: source.targetCardinality as DiagramEdge['targetCardinality'],
    relationshipType: source.relationshipType as DiagramEdge['relationshipType'],
    bpmnFlowType: source.bpmnFlowType as DiagramEdge['bpmnFlowType'],
    bundleId: typeof source.bundleId === 'string' && source.bundleId.trim() ? source.bundleId.trim().slice(0, 160) : null,
  };
}

export function recoverDocument(raw: unknown): RecoveryResult {
  const strict = validateDocument(raw);
  if (strict.valid) return { ok: true, document: normalizeDocument(raw as DiagramDocument), changes: [], errors: [] };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ok: false, changes: [], errors: strict.errors };
  const source = raw as Record<string, unknown>;
  if (source.schemaVersion !== undefined && source.schemaVersion !== 1) return { ok: false, changes: [], errors: ['Versão de arquivo incompatível com este OWDEX Draw.'] };
  if (!Array.isArray(source.pages) || source.pages.length === 0 || source.pages.length > 1000) return { ok: false, changes: [], errors: strict.errors };

  const changes: string[] = [];
  const pageIds = new Set<string>();
  const pageIdRemap = new Map<string, string>();
  const pages: DiagramPage[] = [];

  source.pages.forEach((rawPage, pageIndex) => {
    if (!rawPage || typeof rawPage !== 'object' || Array.isArray(rawPage)) return;
    const pageSource = rawPage as Record<string, unknown>;
    let pageId = safeId(pageSource.id) ? String(pageSource.id) : uuid();
    const originalPageId = text(pageSource.id);
    if (pageIds.has(pageId)) { pageId = uuid(); changes.push(`Página ${pageIndex + 1}: ID duplicado foi substituído.`); }
    if (originalPageId) pageIdRemap.set(originalPageId, pageId);
    pageIds.add(pageId);

    const nodeIds = new Set<string>();
    const nodeRemap = new Map<string, string>();
    const nodes: DiagramNode[] = [];
    const rawNodes = Array.isArray(pageSource.nodes) ? pageSource.nodes.slice(0, 100_000) : [];
    rawNodes.forEach((rawNode, nodeIndex) => {
      const recovered = recoverNode(rawNode, pageIndex, nodeIndex, changes);
      if (!recovered) { changes.push(`Página ${pageIndex + 1}: objeto ${nodeIndex + 1} incompatível foi ignorado.`); return; }
      const originalId = rawNode && typeof rawNode === 'object' ? text((rawNode as Record<string, unknown>).id) : '';
      if (nodeIds.has(recovered.id)) {
        recovered.id = uuid();
        changes.push(`Página ${pageIndex + 1}: ID duplicado de objeto foi substituído; conexões existentes permanecem vinculadas ao primeiro objeto com esse ID.`);
      } else if (originalId) nodeRemap.set(originalId, recovered.id);
      nodeIds.add(recovered.id);
      nodes.push(recovered);
    });

    const edgeIds = new Set<string>();
    const edges: DiagramEdge[] = [];
    const rawEdges = Array.isArray(pageSource.edges) ? pageSource.edges.slice(0, 200_000) : [];
    rawEdges.forEach((rawEdge, edgeIndex) => {
      const recovered = recoverEdge(rawEdge, nodeIds, nodeRemap, pageIndex, edgeIndex, changes);
      if (!recovered) return;
      if (edgeIds.has(recovered.id)) { recovered.id = uuid(); changes.push(`Página ${pageIndex + 1}: ID duplicado de conexão foi substituído.`); }
      edgeIds.add(recovered.id);
      edges.push(recovered);
    });

    pages.push({ id: pageId, name: text(pageSource.name, `Página ${pageIndex + 1}`).slice(0, 500), nodes, edges });
  });

  if (!pages.length) return { ok: false, changes, errors: ['Nenhuma página recuperável foi encontrada.'] };

  for (const page of pages) {
    for (const node of page.nodes) {
      const linked = typeof node.data.linkPageId === 'string' ? node.data.linkPageId : '';
      if (!linked) continue;
      const mapped = pageIdRemap.get(linked) ?? linked;
      if (pageIds.has(mapped)) node.data.linkPageId = mapped;
      else { delete node.data.linkPageId; changes.push(`Link interno quebrado removido do objeto “${node.text || node.id}”.`); }
    }
  }

  const now = new Date().toISOString();
  const originalActive = text(source.activePageId);
  const activePageId = pageIdRemap.get(originalActive) ?? (pageIds.has(originalActive) ? originalActive : pages[0].id);
  if (activePageId !== originalActive) changes.push('Página ativa inválida foi ajustada.');

  const candidate: DiagramDocument = {
    schemaVersion: 1,
    id: safeId(source.id) ? String(source.id) : uuid(),
    title: text(source.title, 'Diagrama recuperado').slice(0, 1000),
    activePageId,
    pages,
    createdAt: typeof source.createdAt === 'string' && !Number.isNaN(Date.parse(source.createdAt)) ? source.createdAt : now,
    updatedAt: now,
    metadata: { app: 'OWDEX Draw', version: '1.0.0-rc3' },
  };
  if (!safeId(source.id)) changes.push('ID do documento foi recriado.');
  if (!source.createdAt || Number.isNaN(Date.parse(String(source.createdAt)))) changes.push('Data de criação inválida foi reparada.');

  const normalized = normalizeDocument(candidate);
  const finalValidation = validateDocument(normalized);
  if (!finalValidation.valid) return { ok: false, changes, errors: finalValidation.errors };
  return { ok: true, document: normalized, changes: Array.from(new Set(changes)).slice(0, 100), errors: [] };
}
